import { useEffect } from 'react';
import { useColorScheme } from '@mui/material/styles';
import { palette } from '@/theme/tokens';

/** Marks the tag this hook owns, so it never fights the static ones in index.html. */
const OWNED = 'data-runtime-theme-color';

const SURFACE = {
  light: palette.paper,
  dark: palette.night,
} as const;

/**
 * Keeps the browser chrome the same colour as the page.
 *
 * `index.html` ships two `theme-color` tags keyed to `prefers-color-scheme`, which
 * is right until a visitor overrides the scheme - at which point the OS still drives
 * the chrome and a light page sits under a dark address bar on mobile.
 *
 * A `theme-color` without a `media` attribute always matches, and the browser takes
 * the first tag that matches, so inserting one at the top of `<head>` wins over both
 * static tags. It is only inserted once an explicit choice exists; while the mode is
 * `system` the static tags are already correct and are left to do their job.
 */
export function useThemeColorMeta(): void {
  const { mode } = useColorScheme();

  useEffect(() => {
    const existing = document.head.querySelector(`meta[${OWNED}]`);

    // No explicit choice - the media-query tags in index.html are already correct.
    if (!mode || mode === 'system') {
      existing?.remove();
      return;
    }

    const tag = existing ?? document.createElement('meta');
    tag.setAttribute('name', 'theme-color');
    tag.setAttribute(OWNED, '');
    tag.setAttribute('content', SURFACE[mode]);

    if (!existing) {
      document.head.insertBefore(tag, document.head.firstChild);
    }
  }, [mode]);
}
